import { PasskeyError } from "./types.js";
import { parseDERSignature } from "./encoding.js";
import { fromHex, toHex, encodeAbiParameters } from "viem";
/**
 * Get a properly formatted signature for Safe transactions, with special handling for WebAuthn signatures
 */
export async function getWebAuthnSignature(safeTxHash, passkey) {
    console.log("🔐 Starting WebAuthn signature generation...");
    try {
        // Use passkey.getFn to get the WebAuthn credential attestation
        if (!passkey.getFn) {
            throw new PasskeyError("Passkey getFn is not available", "PASSKEY_UNAVAILABLE");
        }
        const credential = (await passkey.getFn({
            publicKey: {
                challenge: fromHex(safeTxHash, "bytes"),
                userVerification: "preferred",
            },
        }));
        if (!credential || !credential.response) {
            throw new PasskeyError("Failed to get WebAuthn credential", "CREDENTIAL_FAILED");
        }
        const response = credential.response;
        // Extract the components from the credential response
        const authenticatorData = new Uint8Array(response.authenticatorData);
        const clientDataJSON = new Uint8Array(response.clientDataJSON);
        const signature = new Uint8Array(response.signature);
        // Extract client data fields using helper function
        const clientDataFields = extractClientDataFields(clientDataJSON);
        // Parse DER signature to extract r and s
        const { r, s } = parseDERSignature(signature);
        // Normalize signature using helper function
        const { r: normalizedR, s: normalizedS } = normalizeSignature(r, s);
        // Create Safe WebAuthn signature using helper function
        const safeWebAuthnSignature = createSafeWebAuthnSignature(authenticatorData, clientDataFields, normalizedR, normalizedS);
        // Return the raw Safe WebAuthn signature (test shows this validates correctly)
        return {
            signature: safeWebAuthnSignature,
            clientDataJSON: toHex(clientDataJSON),
            authenticatorData: toHex(authenticatorData),
        };
    }
    catch (error) {
        console.error("❌ Error processing WebAuthn signature:", error);
        if (error instanceof PasskeyError) {
            throw error;
        }
        throw new PasskeyError(`WebAuthn signature generation failed: ${error instanceof Error ? error.message : "Unknown error"}`, "SIGNATURE_GENERATION_FAILED", error);
    }
}
/**
 * Normalize ECDSA signature to low-s form (required for Safe validation)
 */
export function normalizeSignature(r, s) {
    const secp256r1Order = BigInt("0xffffffff00000000ffffffffffffffffbce6faada7179e84f3b9cac2fc632551");
    const sBigInt = BigInt(s);
    const halfOrder = secp256r1Order / 2n;
    let normalizedS = s;
    if (sBigInt > halfOrder) {
        const lowS = secp256r1Order - sBigInt;
        normalizedS = "0x" + lowS.toString(16).padStart(64, "0");
    }
    return { r, s: normalizedS };
}
/**
 * Create Safe WebAuthn signature format
 */
export function createSafeWebAuthnSignature(authenticatorData, clientDataFields, r, s) {
    return encodeAbiParameters([
        { name: "authenticatorData", type: "bytes" },
        { name: "clientDataFields", type: "string" },
        { name: "r", type: "uint256" },
        { name: "s", type: "uint256" },
    ], [toHex(authenticatorData), clientDataFields, BigInt(r), BigInt(s)]);
}
/**
 * Extract client data fields from WebAuthn response (excluding type and challenge)
 */
export function extractClientDataFields(clientDataJSON) {
    const clientDataString = new TextDecoder().decode(clientDataJSON);
    const clientData = JSON.parse(clientDataString);
    return Object.entries(clientData)
        .filter(([key]) => key !== "type" && key !== "challenge")
        .map(([key, value]) => `"${key}":${JSON.stringify(value)}`)
        .join(",");
}
